import styled, { css } from 'styled-components';

/**
 * Styled tokens shared between the «detail-page control centers»
 * (StudentDetail, GroupDetail). Originally lived in
 * `StudentDetail.styled.ts`; promoted here so both pages render their
 * HeaderBar, KpiStrip, inline popups, and section tables with one set
 * of tokens.
 *
 * Page-local compositions (e.g. `AgreementCardWrap`, `AgreementHeader`
 * for StudentDetail) stay in the page's own `.styled.ts` to keep
 * page-specific layout out of the shared surface.
 */

export const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: auto;
`;

export const PrimaryActionButton = styled.button<{
  tone?: 'primary' | 'ghost';
}>`
  border: 1px solid
    ${({ tone }) => (tone === 'primary' ? '#2563eb' : '#e2e8f0')};
  background: ${({ tone }) => (tone === 'primary' ? '#2563eb' : '#fff')};
  color: ${({ tone }) => (tone === 'primary' ? '#fff' : '#0f172a')};
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, border-color 0.12s;

  &:hover:not(:disabled) {
    background: ${({ tone }) => (tone === 'primary' ? '#1d4ed8' : '#f8fafc')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

export const ProgressTrack = styled.div`
  width: 100%;
  height: 8px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
  margin: 8px 0 4px;
`;

export const ProgressFill = styled.div<{
  pct: number;
  tone: 'ok' | 'warn' | 'danger';
}>`
  height: 100%;
  width: ${({ pct }) => Math.max(0, Math.min(100, pct))}%;
  background: ${({ tone }) =>
    tone === 'danger' ? '#dc2626' : tone === 'warn' ? '#d97706' : '#16a34a'};
  transition: width 0.25s;
`;

export const Subblock = styled.div`
  margin-top: 16px;
`;

export const SubblockHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
`;

export const SubblockHeaderCount = styled.span`
  color: #94a3b8;
  font-weight: 600;
`;

export const SubblockRight = styled.div`
  margin-left: auto;
  display: flex;
  gap: 6px;
`;

export const SmallTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;

  thead th {
    text-align: left;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #94a3b8;
    font-weight: 600;
    padding: 6px 8px;
    border-bottom: 1px solid #e2e8f0;
  }

  tbody td {
    padding: 8px;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }
`;

export const OrphanRowTone = styled.tr`
  background: #fff7ed;
`;

export const PaymentLink = styled.a`
  color: #16a34a;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

export const Muted = styled.span`
  color: #94a3b8;
`;

export const ChipLink = styled.button`
  border: 0;
  background: transparent;
  color: #2563eb;
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export const Tag = styled.span<{ tone?: 'ok' | 'warn' | 'danger' | 'neutral' }>`
  display: inline-block;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-radius: 999px;
  ${({ tone = 'neutral' }) => {
    if (tone === 'ok')
      return css`
        background: #dcfce7;
        color: #166534;
      `;
    if (tone === 'warn')
      return css`
        background: #ffedd5;
        color: #9a3412;
      `;
    if (tone === 'danger')
      return css`
        background: #fee2e2;
        color: #991b1b;
      `;
    return css`
      background: #f1f5f9;
      color: #475569;
    `;
  }}
`;

/* ============ Inline popup form bits ============ */

export const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 4px 0;
`;

export const FormLabel = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #475569;
  font-weight: 600;
`;

export const FormInput = styled.input`
  height: 36px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0 10px;
  font-size: 14px;
  background: #fff;
  outline: none;
  font-weight: 500;
  color: #0f172a;

  &:focus {
    border-color: #2563eb;
  }
`;

export const FormSelect = styled.select`
  height: 36px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0 10px;
  font-size: 14px;
  background: #fff;
  outline: none;
  font-weight: 500;
  color: #0f172a;

  &:focus {
    border-color: #2563eb;
  }
`;

export const FormFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 16px;
`;

export const InlineNotice = styled.div`
  font-size: 12px;
  color: #9a3412;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  padding: 8px 10px;
  line-height: 1.4;
`;
